/**
 * 제품 — 기업 관리자 화면
 * ---------------------------------------------------------------------------
 * 두 가지를 한곳에서 본다.
 *
 *   1. 지금 쓰고 있는 제품과 **바로가기**
 *   2. 더 쓸 수 있는 제품과 요금제 — 여기서 바로 신청한다
 *
 * 쓰고 있는 것을 위에 둔다. 매일 들어오는 이유는 신청이 아니라 바로가기다.
 *
 * 결제 문구
 *   PG 연동 전에는 "신청" 이라고만 쓴다. "결제" 라고 쓰면 카드가 빠져나갔다고
 *   오해한다. 실제 청구는 월 정산으로 나간다.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App as AntdApp,
  Badge,
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Modal,
  Radio,
  Row,
  Space,
  Statistic,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  CheckCircleOutlined,
  ExportOutlined,
  ShoppingOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import { apiClient } from '../../lib/api-client';
import { errorText } from '../../lib/queries';
import { CodeTag, useCodes, type Codes } from '../../lib/codes';

const { Text, Paragraph, Title } = Typography;

/* =========================================================================
 * 형
 * ====================================================================== */

interface StorePlan {
  planId: string;
  planCode: string;
  planName: string;
  description: string | null;
  planType: string;
  billingCycle: string;
  billingUnit: string;
  unitLabel: string | null;
  basePrice: number;
  unitPrice: number;
  includedUnits: number;
  minUnits: number;
  maxUnits: number | null;
  trialDays: number;
  modules: string[];
}

interface StoreProduct {
  productCode: string;
  productName: string;
  description: string | null;
  iconName: string | null;
  vendor: string | null;
  baseUrl: string | null;
  subscription: {
    subscriptionId: string;
    status: string;
    planId: string;
    planCode: string;
    planName: string;
    units: number;
    unitLabel: string | null;
    monthlyAmount: number;
    startedAt: string;
    currentPeriodEnd: string;
    trialEndsAt: string | null;
    cancelAtPeriodEnd: boolean;
    modules: string[];
  } | null;
  plans: StorePlan[];
}

interface StoreResponse {
  paymentEnabled: boolean;
  products: StoreProduct[];
}

/* =========================================================================
 * 표시 도우미
 * ====================================================================== */

/**
 * 표시명과 색은 공통코드가 정한다(「시스템 관리 > 공통코드」).
 *
 * 청구 주기는 값 칸(item_value)에 짧은 표기를 둔다 — 요금 문구는
 * "월 99,000원" 처럼 한 글자가 낫고, 목록의 표시명은 "월간" 이 낫다.
 * 값이 비어 있으면 표시명을 쓴다.
 */
const CODE_GROUPS = ['SUBSCRIPTION_STATUS', 'BILLING_CYCLE'];

const won = (n: number) => `${Math.round(n).toLocaleString('ko-KR')}원`;
const date = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('ko-KR') : '-');

/** 요금제 한 줄 설명. 과금 방식마다 읽는 법이 다르다. */
function priceText(plan: StorePlan, codes: Codes): string {
  const cycle =
    codes.value('BILLING_CYCLE', plan.billingCycle) ??
    codes.label('BILLING_CYCLE', plan.billingCycle);

  if (plan.billingUnit === 'FLAT' || plan.unitPrice === 0) {
    return plan.basePrice === 0 ? '무료' : `${cycle} ${won(plan.basePrice)}`;
  }

  const unit = plan.unitLabel ?? '단위';
  const included = plan.includedUnits > 0 ? ` (${plan.includedUnits}${unit} 포함)` : '';
  return `${cycle} ${won(plan.basePrice)}${included} + ${unit}당 ${won(plan.unitPrice)}`;
}

/* =========================================================================
 * 화면
 * ====================================================================== */

export default function ProductStorePage() {
  const { message, modal } = AntdApp.useApp();
  const queryClient = useQueryClient();
  const codes = useCodes(CODE_GROUPS);

  const [target, setTarget] = useState<StoreProduct | null>(null);
  const [chosenPlan, setChosenPlan] = useState<string | null>(null);

  const store = useQuery<StoreResponse>({
    queryKey: ['store', 'products'],
    queryFn: async () => (await apiClient.get<StoreResponse>('/store/products')).data,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['store'] });

  const subscribe = useMutation({
    mutationFn: async (planId: string) => apiClient.post('/store/subscribe', { planId }),
    onSuccess: async () => {
      message.success('신청이 완료되었습니다. 바로 사용하실 수 있습니다.');
      setTarget(null);
      await refresh();
    },
    onError: (err) => message.error(errorText(err as never)),
  });

  const cancel = useMutation({
    mutationFn: async (productCode: string) =>
      apiClient.post(`/store/${productCode}/cancel`, { reason: '고객사 해지 요청' }),
    onSuccess: async () => {
      message.success('이번 이용 기간이 끝나면 해지됩니다.');
      await refresh();
    },
    onError: (err) => message.error(errorText(err as never)),
  });

  const resume = useMutation({
    mutationFn: async (productCode: string) => apiClient.post(`/store/${productCode}/resume`),
    onSuccess: async () => {
      message.success('해지 예약을 취소했습니다.');
      await refresh();
    },
    onError: (err) => message.error(errorText(err as never)),
  });

  const products = store.data?.products ?? [];
  const mine = useMemo(() => products.filter((p) => p.subscription), [products]);
  const available = useMemo(() => products.filter((p) => !p.subscription), [products]);

  const monthlyTotal = mine.reduce((sum, p) => sum + (p.subscription?.monthlyAmount ?? 0), 0);

  const openStore = (product: StoreProduct) => {
    setTarget(product);
    setChosenPlan(product.subscription?.planId ?? product.plans[0]?.planId ?? null);
  };

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <div>
        <Title level={4} style={{ margin: 0 }}>
          제품
        </Title>
        <Text type="secondary">
          쓰고 있는 제품으로 바로 이동하거나, 새 제품을 신청합니다.
        </Text>
      </div>

      {store.isError && <Alert type="error" showIcon message={errorText(store.error as never)} />}

      {store.data && !store.data.paymentEnabled && (
        <Alert
          type="info"
          showIcon
          message="신청하면 바로 사용할 수 있습니다"
          description="카드 결제는 아직 연결되어 있지 않습니다. 이용료는 월 정산으로 청구됩니다."
        />
      )}

      {/* ---- 쓰고 있는 제품 ---- */}
      <Card
        title="이용 중인 제품"
        extra={
          mine.length > 0 && (
            <Statistic
              value={monthlyTotal}
              suffix="원 / 월"
              valueStyle={{ fontSize: 16 }}
              style={{ display: 'inline-block' }}
            />
          )
        }
        loading={store.isLoading}
      >
        {mine.length === 0 ? (
          <Empty description="아직 이용 중인 제품이 없습니다. 아래에서 선택해 주세요." />
        ) : (
          <Row gutter={[16, 16]}>
            {mine.map((p) => {
              const sub = p.subscription!;

              return (
                <Col key={p.productCode} xs={24} lg={12} xxl={8}>
                  <Card
                    size="small"
                    style={{ height: '100%' }}
                    title={
                      <Space>
                        <Text strong>{p.productName}</Text>
                        <CodeTag codes={codes} group="SUBSCRIPTION_STATUS" value={sub.status} />
                        {sub.cancelAtPeriodEnd && <Tag>해지 예약</Tag>}
                      </Space>
                    }
                    extra={
                      p.baseUrl ? (
                        <Button
                          type="primary"
                          size="small"
                          icon={<ExportOutlined />}
                          href={p.baseUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          바로가기
                        </Button>
                      ) : (
                        <Tooltip title="아직 접속 주소가 등록되지 않았습니다">
                          <Button size="small" disabled>
                            바로가기
                          </Button>
                        </Tooltip>
                      )
                    }
                  >
                    <Descriptions size="small" column={1} colon={false}>
                      <Descriptions.Item label="요금제">
                        {sub.planName}
                        {sub.units > 1 && (
                          <Text type="secondary">
                            {' '}
                            · {sub.units}
                            {sub.unitLabel ?? ''}
                          </Text>
                        )}
                      </Descriptions.Item>
                      <Descriptions.Item label="월 이용료">
                        {sub.monthlyAmount === 0 ? '무료' : won(sub.monthlyAmount)}
                      </Descriptions.Item>
                      <Descriptions.Item label="이용 시작">{date(sub.startedAt)}</Descriptions.Item>
                      <Descriptions.Item
                        label={sub.cancelAtPeriodEnd ? '해지 예정' : '다음 갱신'}
                      >
                        {date(sub.currentPeriodEnd)}
                      </Descriptions.Item>
                      {sub.trialEndsAt && (
                        <Descriptions.Item label="체험 종료">
                          {date(sub.trialEndsAt)}
                        </Descriptions.Item>
                      )}
                    </Descriptions>

                    {p.baseUrl && (
                      <Paragraph
                        copyable={{ text: p.baseUrl }}
                        type="secondary"
                        style={{ fontSize: 12, marginBottom: 8 }}
                      >
                        {p.baseUrl}
                      </Paragraph>
                    )}

                    <Space size={4} wrap>
                      <Button size="small" onClick={() => openStore(p)}>
                        요금제 변경
                      </Button>
                      {sub.cancelAtPeriodEnd ? (
                        <Button
                          size="small"
                          icon={<UndoOutlined />}
                          loading={resume.isPending}
                          onClick={() => resume.mutate(p.productCode)}
                        >
                          해지 취소
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          danger
                          onClick={() =>
                            modal.confirm({
                              title: `${p.productName} 을(를) 해지할까요?`,
                              content: `${date(sub.currentPeriodEnd)} 까지는 그대로 쓰실 수 있고, 그 뒤에 멈춥니다. 그전에는 해지를 취소할 수 있습니다.`,
                              okText: '해지 예약',
                              cancelText: '그만두기',
                              okButtonProps: { danger: true },
                              onOk: () => cancel.mutate(p.productCode),
                            })
                          }
                        >
                          해지
                        </Button>
                      )}
                    </Space>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}
      </Card>

      {/* ---- 더 쓸 수 있는 제품 ---- */}
      <Card title="추가할 수 있는 제품" loading={store.isLoading}>
        {available.length === 0 ? (
          <Empty description="모든 제품을 이용 중입니다." />
        ) : (
          <Row gutter={[16, 16]}>
            {available.map((p) => {
              const cheapest = [...p.plans].sort((a, b) => a.basePrice - b.basePrice)[0];
              const trial = p.plans.find((pl) => pl.trialDays > 0);

              return (
                <Col key={p.productCode} xs={24} sm={12} lg={8}>
                  <Card size="small" style={{ height: '100%' }}>
                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                      <Space style={{ justifyContent: 'space-between', width: '100%' }}>
                        <Text strong style={{ fontSize: 15 }}>
                          {p.productName}
                        </Text>
                        {trial && <Badge count={`${trial.trialDays}일 체험`} color="blue" />}
                      </Space>

                      <Text type="secondary" style={{ fontSize: 12, minHeight: 36, display: 'block' }}>
                        {p.description ?? '설명이 등록되지 않았습니다.'}
                      </Text>

                      {cheapest ? (
                        <Text>{priceText(cheapest, codes)}</Text>
                      ) : (
                        <Text type="secondary">공개된 요금제가 없습니다</Text>
                      )}

                      <Button
                        type="primary"
                        block
                        icon={<ShoppingOutlined />}
                        disabled={p.plans.length === 0}
                        onClick={() => openStore(p)}
                      >
                        {p.plans.length === 0 ? '문의 필요' : '신청하기'}
                      </Button>
                    </Space>
                  </Card>
                </Col>
              );
            })}
          </Row>
        )}
      </Card>

      {/* ---- 요금제 선택 ---- */}
      <Modal
        open={!!target}
        title={target ? `${target.productName} 요금제 선택` : ''}
        onCancel={() => setTarget(null)}
        okText={target?.subscription ? '요금제 변경' : '신청하기'}
        cancelText="취소"
        confirmLoading={subscribe.isPending}
        okButtonProps={{
          disabled: !chosenPlan || chosenPlan === target?.subscription?.planId,
        }}
        onOk={() => chosenPlan && subscribe.mutate(chosenPlan)}
        width={620}
        destroyOnClose
      >
        {target && (
          <Space direction="vertical" size={12} style={{ width: '100%', marginTop: 12 }}>
            {target.subscription && (
              <Alert
                type="info"
                showIcon
                message={`지금은 ${target.subscription.planName} 을(를) 이용 중입니다`}
              />
            )}

            <Radio.Group
              value={chosenPlan}
              onChange={(e) => setChosenPlan(e.target.value)}
              style={{ width: '100%' }}
            >
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                {target.plans.map((plan) => (
                  <Radio.Button
                    key={plan.planId}
                    value={plan.planId}
                    style={{ width: '100%', height: 'auto', padding: 12 }}
                  >
                    <Space direction="vertical" size={2} style={{ width: '100%' }}>
                      <Space>
                        <Text strong>{plan.planName}</Text>
                        {plan.planId === target.subscription?.planId && (
                          <Tag icon={<CheckCircleOutlined />} color="green">
                            이용 중
                          </Tag>
                        )}
                        {plan.trialDays > 0 && <Tag color="blue">{plan.trialDays}일 체험</Tag>}
                      </Space>
                      <Text>{priceText(plan, codes)}</Text>
                      {plan.description && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {plan.description}
                        </Text>
                      )}
                    </Space>
                  </Radio.Button>
                ))}
              </Space>
            </Radio.Group>

            <Text type="secondary" style={{ fontSize: 12 }}>
              {store.data?.paymentEnabled
                ? '신청 즉시 결제가 진행됩니다.'
                : '신청하면 바로 사용할 수 있고, 이용료는 월 정산으로 청구됩니다.'}
            </Text>
          </Space>
        )}
      </Modal>
    </Space>
  );
}
